import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import WavyEnd from "../components/WavyEnd";
import SuburbInput from "../components/SuburbInput";

// Structured options for the enrichment dropdowns — kept in the file
// rather than a shared lib because Contact is the only consumer.
const PROJECT_TYPES = [
  "New build",
  "Alteration / Addition",
  "Interior only",
  "Fit-out / Commercial",
  "Not sure yet",
];
const HOUSE_SIZES = [
  "Under 200 sqm",
  "200 – 400 sqm",
  "400 – 600 sqm",
  "600+ sqm",
  "Not applicable",
];
const SERVICES = [
  "Residential Architecture",
  "Residential Interiors",
  "Commercial Architecture",
  "Commercial Fit-outs",
  "Development Application (DA)",
  "Complying Development (CDC)",
  "Feasibility & Cost Planning",
  "Not sure yet",
];

const AU_PHONE = /^(?:(?:\+61|61|0)[2-478]\d{8}|1[38]00\d{6}|13\d{4})$/;
const isAuPhone = (v) => AU_PHONE.test((v || "").replace(/[\s\-().]/g, ""));

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  message: "",
  website: "", // honeypot
  suburb: "",
  project_type: "",
  house_size: "",
  service: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please complete name, email, phone and project message.");
      return;
    }
    if (!isAuPhone(form.phone)) {
      toast.error("Please enter a valid Australian phone number, e.g. 0412 345 678.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message received. We'll respond within a week.");
      setForm(EMPTY_FORM);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email directly.");
    } finally {
      setSending(false);
    }
  };

  // Shared select styles — hairline base, focus:border-marine, no rounded corners
  const selectCls = "w-full bg-transparent hairline-b py-3 focus:outline-none focus:border-marine text-lg appearance-none pr-8 bg-no-repeat";
  const selectStyle = {
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%2316403F' stroke-width='1.2' fill='none'/></svg>\")",
    backgroundPosition: "right 4px center",
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 container-w">
      <div className="mono-label mb-4">Contact</div>
      <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-6 max-w-4xl">
        A conversation, first.
      </h1>
      <WavyEnd className="text-marine mb-16 md:mb-24" width={200} />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 pb-32 md:pb-48">
        {/* Details */}
        <aside className="md:col-span-4 space-y-10">
          <div>
            <div className="mono-label mb-2">Email</div>
            <a href="mailto:info@thewhiteprojectco.com" className="link-underline text-lg" data-testid="contact-email-link">
              info@thewhiteprojectco.com
            </a>
          </div>
          <div>
            <div className="mono-label mb-2">Studio</div>
            <div className="text-lg">Sydney NSW,<br />Australia</div>
          </div>
          <div>
            <div className="mono-label mb-2">Elsewhere</div>
            <a href="https://instagram.com/thewhiteprojectco" target="_blank" rel="noreferrer" className="link-underline text-lg" data-testid="contact-instagram">
              @thewhiteprojectco
            </a>
          </div>
          <div>
            <div className="mono-label mb-2">Registered</div>
            <div className="text-ash">ABN 12 656 275 697</div>
            <div className="text-ash mt-1">Established 2021</div>
          </div>
        </aside>

        {/* Form */}
        <div className="md:col-span-7 md:col-start-6">
          {sent ? (
            <div className="hairline-t hairline-b py-24 md:py-32 text-center" data-testid="contact-thanks">
              <div className="mono-label mb-6">Received</div>
              <p className="font-display text-3xl md:text-4xl max-w-lg mx-auto leading-tight">
                Thank you. We&rsquo;ll respond within a week.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden
              />

              {/* --- Required --- */}
              <label className="block">
                <span className="mono-label block mb-2">Your name <span className="text-marine">*</span></span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  data-testid="contact-name"
                  className="w-full bg-transparent hairline-b py-3 focus:outline-none focus:border-marine text-lg"
                  required
                />
              </label>

              <label className="block">
                <span className="mono-label block mb-2">Email <span className="text-marine">*</span></span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  data-testid="contact-email"
                  className="w-full bg-transparent hairline-b py-3 focus:outline-none focus:border-marine text-lg"
                  required
                />
              </label>

              <label className="block">
                <span className="mono-label block mb-2">Phone <span className="text-marine">*</span></span>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="0412 345 678"
                  maxLength={30}
                  data-testid="contact-phone"
                  className="w-full bg-transparent hairline-b py-3 focus:outline-none focus:border-marine text-lg"
                  required
                />
              </label>

              {/* --- Project details (optional but helpful) --- */}
              <div className="pt-4 mono-label text-ash">Project details <span className="text-ash/50">— optional, helps us respond in one</span></div>

              <label className="block">
                <span className="mono-label block mb-2">Suburb</span>
                <SuburbInput
                  value={form.suburb}
                  onChange={(v) => set("suburb", v)}
                  onCommit={(v) => set("suburb", v)}
                  className="w-full bg-transparent hairline-b py-3 focus:outline-none focus:border-marine text-lg"
                  placeholder="Start typing your suburb…"
                  data-testid="contact-suburb"
                />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <label className="block">
                  <span className="mono-label block mb-2">Project type</span>
                  <select
                    value={form.project_type}
                    onChange={(e) => set("project_type", e.target.value)}
                    data-testid="contact-project-type"
                    className={selectCls}
                    style={selectStyle}
                  >
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>

                <label className="block">
                  <span className="mono-label block mb-2">Size of home</span>
                  <select
                    value={form.house_size}
                    onChange={(e) => set("house_size", e.target.value)}
                    data-testid="contact-house-size"
                    className={selectCls}
                    style={selectStyle}
                  >
                    <option value="">Select…</option>
                    {HOUSE_SIZES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="mono-label block mb-2">Service you're most interested in</span>
                <select
                  value={form.service}
                  onChange={(e) => set("service", e.target.value)}
                  data-testid="contact-service"
                  className={selectCls}
                  style={selectStyle}
                >
                  <option value="">Select…</option>
                  {SERVICES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>

              <label className="block">
                <span className="mono-label block mb-2">Tell us about the project <span className="text-marine">*</span></span>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  data-testid="contact-message"
                  className="w-full bg-transparent hairline-b py-3 focus:outline-none focus:border-marine text-lg resize-none"
                  required
                  placeholder="Timeline, budget range, references you like, anything else that would help us respond well."
                />
              </label>

              <div className="flex items-center justify-between pt-4 flex-wrap gap-4">
                <p className="mono-label text-ash max-w-xs">Your information is used solely for this enquiry per the Australian Privacy Act.</p>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="mono-label px-8 py-4 hairline text-marine hover:bg-marine hover:text-paper transition-colors duration-500 disabled:opacity-40"
                >
                  {sending ? "Sending…" : "Send enquiry →"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
